import { Menu, ShoppingBag, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useActiveSection } from "../../context/ActiveSectionContext";
import { NAV_LINKS } from "../../data/site";
import { useScrolled } from "../../hooks/useScroll";
import { cn } from "../../utils/format";
import { CartButton } from "../cart/CartButton";
import { Button } from "../ui/Button";
import { Logo } from "./Logo";

export const Navbar = () => {
  const location = useLocation();
  const scrolled = useScrolled(24);
  const activeSection = useActiveSection();
  const [menuOpen, setMenuOpen] = useState(false);

  const isHome = location.pathname === "/";
  const activeLabel = isHome
    ? NAV_LINKS.find((link) => link.sectionId === activeSection)?.label ?? "Home"
    : NAV_LINKS.find((link) => !link.to.includes("#") && link.to !== "/" && location.pathname.startsWith(link.to))
        ?.label;

  // Fecha o menu mobile ao navegar.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.key]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => event.key === "Escape" && setMenuOpen(false);
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color] duration-300",
        scrolled || menuOpen
          ? "border-line/70 bg-night/75 backdrop-blur-xl"
          : "border-transparent bg-transparent",
      )}
    >
      <nav
        aria-label="Principal"
        className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:h-20 lg:px-8"
      >
        <Logo />

        <ul className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((link) => {
            const active = link.label === activeLabel;
            return (
              <li key={link.to}>
                <Link
                  to={link.to}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "relative block px-3 py-2 text-sm font-medium transition-colors",
                    active ? "text-primary" : "text-white/80 hover:text-white",
                  )}
                >
                  {link.label}
                  {active && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full bg-primary"
                      transition={{ type: "spring", stiffness: 500, damping: 38 }}
                    />
                  )}
                </Link>
              </li>
            );
          })}
        </ul>

        <div className="flex items-center gap-2 sm:gap-3">
          <CartButton />
          <Button
            to="/cardapio"
            icon={<ShoppingBag className="size-4" aria-hidden="true" />}
            iconPosition="left"
            className="hidden sm:inline-flex"
          >
            Pedir online
          </Button>
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-controls="menu-mobile"
            aria-label={menuOpen ? "Fechar menu" : "Abrir menu"}
            className="grid size-11 place-items-center rounded-full border border-line text-white transition-colors hover:border-primary hover:text-primary lg:hidden"
          >
            {menuOpen ? <X className="size-5" aria-hidden="true" /> : <Menu className="size-5" aria-hidden="true" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            id="menu-mobile"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.22 }}
            className="border-t border-line/70 lg:hidden"
          >
            <ul className="mx-auto flex max-w-7xl flex-col px-4 py-4 sm:px-6">
              {NAV_LINKS.map((link, i) => (
                <motion.li
                  key={link.to}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.03 * i }}
                >
                  <Link
                    to={link.to}
                    aria-current={link.label === activeLabel ? "page" : undefined}
                    className={cn(
                      "flex items-center justify-between border-b border-line/60 py-3.5 font-display text-2xl tracking-wide",
                      link.label === activeLabel ? "text-primary" : "text-white",
                    )}
                  >
                    {link.label}
                  </Link>
                </motion.li>
              ))}
            </ul>
            <div className="mx-auto max-w-7xl px-4 pb-6 sm:hidden">
              <Button
                to="/cardapio"
                icon={<ShoppingBag className="size-4" aria-hidden="true" />}
                iconPosition="left"
                className="w-full"
              >
                Pedir online
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
